import { Container, REFERENCE_FPS, Vector2 } from '@cgs/wge';
import {
  T_ISlotGameEngineProvider,
  T_StackedReelsStopSlotActionConfig,
  T_StagedFreeSpinsCollectConfig,
} from '../../type_definitions';
import { ISlotGameEngineProvider } from '../game_components_providers/i_slot_game_engine_provider';
import { ReelsEngine } from '../reels_engine';
import { AbstractAnticipationConfig } from './abstract_anticipation_config';
import { AbstractGameConfig } from './abstract_game_config';
import { AbstractSpinConfig } from './abstract_spin_config';
import { AbstractStaticConfig } from './abstract_static_config';
import { GameModuleConfig } from './game_module_config';

export class ReelsEngineGameConfig implements AbstractGameConfig {
  public regularSpinConfig: AbstractSpinConfig;
  public freeSpinConfig: AbstractSpinConfig;
  public iconWithValuesConfig: IconWithValuesConfig;
  public anticipationConfig: AnticipationConfig;
  public staticConfig: StaticConfig;
  public buyFeatureConfig: AbstractSpinConfig | null = null;

  private readonly moduleConfigs: Map<string, GameModuleConfig> = new Map<
    string,
    GameModuleConfig
  >();
  private readonly _namedSpinConfig: Map<string, AbstractSpinConfig> = new Map<
    string,
    AbstractSpinConfig
  >();
  private readonly _configDto: Record<string, any>;
  private readonly fps: number;
  private readonly displayResolution: Vector2;
  private readonly featureConfigs: Map<symbol, any> = new Map<symbol, any>();

  constructor(container: Container, configDto: Record<string, any>, displayResolution: Vector2) {
    this._configDto = configDto;
    this.displayResolution = displayResolution;
    this.fps = REFERENCE_FPS;

    if (this._configDto) {
      if (this._configDto['regularSpin']) {
        this.regularSpinConfig = new SpinConfig(
          container,
          this._configDto['regularSpin'],
          Math.round(this.displayResolution.y)
        );
      }

      if (this._configDto['freeSpin']) {
        this.freeSpinConfig = new SpinConfig(
          container,
          this._configDto['freeSpin'],
          Math.round(this.displayResolution.y)
        );
      }

      if (this._configDto['buyFeature']) {
        this.buyFeatureConfig = new SpinConfig(
          container,
          this._configDto['buyFeature'],
          Math.round(this.displayResolution.y)
        );
      }

      if (this._configDto['iconWithValuesParameters']) {
        this.iconWithValuesConfig = new IconWithValuesConfig(
          this._configDto['iconWithValuesParameters']
        );
      }

      if (this._configDto['anticipationParameters']) {
        this.anticipationConfig = new AnticipationConfig(
          this._configDto['anticipationParameters'],
          this.fps,
          Math.round(this.displayResolution.y)
        );
      }

      if (this._configDto['staticConfig']) {
        this.staticConfig = new StaticConfig(this._configDto['staticConfig']);
      }

      if (this._configDto['moduleConfigs']) {
        this._configDto['moduleConfigs'].forEach(
          (moduleId: string, moduleConfigDto: Map<any, any>) => {
            this.moduleConfigs.set(
              moduleId,
              new GameModuleConfig(container, moduleConfigDto, this.displayResolution)
            );
          }
        );
      }

      if (this._configDto['featuresConfiguration']) {
        const featuresConfiguration = this._configDto['featuresConfiguration'];
        if (featuresConfiguration['stagedFreeSpinsCollectConfig']) {
          this.featureConfigs.set(
            T_StagedFreeSpinsCollectConfig,
            new StagedFreeSpinsCollectConfig(featuresConfiguration['stagedFreeSpinsCollectConfig'])
          );
        }
        if (featuresConfiguration['stackedReelsStopSlotActionConfig']) {
          this.featureConfigs.set(
            T_StackedReelsStopSlotActionConfig,
            new StackedReelsStopSlotActionConfig(
              featuresConfiguration['stackedReelsStopSlotActionConfig']
            )
          );
        }
      }

      if (this._configDto['namedSpinConfigs']) {
        this._configDto['namedSpinConfigs'].forEach((key: string, value: Map<any, any>) => {
          this._namedSpinConfig.set(
            key,
            new SpinConfig(container, value, Math.round(this.displayResolution.y))
          );
        });
      }
    }
  }

  getNamedConfig(name: string): AbstractSpinConfig | null {
    return this._namedSpinConfig.get(name) || null;
  }

  getFakeConfig(name: string): any {
    return this._configDto[name]
      ? new StaticConfig(this._configDto[name])
      : new StaticConfig(this._configDto['staticConfig']);
  }

  getSlotFeatureConfig(type: symbol): any {
    return this.featureConfigs.has(type) ? this.featureConfigs.get(type) : null;
  }
}

export class SpinConfig implements AbstractSpinConfig {
  public multiplier = 1;
  public speedMultiplier = 1;
  private readonly configDto: Record<string, any>;
  private readonly displayHeight: number;
  private readonly fpsCoef = 100 / 60;
  protected _engine: ReelsEngine | null = null;
  protected _container: Container;

  constructor(container: Container, configDto: Record<string, any>, displayHeight: number) {
    this._container = container;
    this.configDto = configDto;
    this.displayHeight = displayHeight;
  }

  get Engine(): ReelsEngine {
    if (!this._engine) {
      this._engine =
        this._container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider).reelsEngine;
    }
    return this._engine;
  }

  get spinedReels(): number[][] {
    return this.configDto['reels'];
  }

  get noWinReels(): number[][] {
    return this.configDto['noWinReels'];
  }

  get spinSpeed(): number {
    const speedFactor = 100;
    return this.configDto['spinSpeed'] * this.speedMultiplier * speedFactor;
  }

  get collapseSpinSpeed(): number {
    const speedFactor = 100;
    return this.configDto['spinSpeed'] * this.speedMultiplier * speedFactor;
  }

  get useAnticipation(): boolean {
    return this.configDto['useAnticipation'];
  }

  get directions(): number[] {
    return this.configDto['directions'];
  }

  get accelerationDuration(): number {
    return this.configDto['spinDuration']['accelerationDuration'] / 1000;
  }

  get spinStartDelay(): number {
    return this.configDto['spinDuration']['spinStartDelay'] / 1000 / this.speedMultiplier;
  }

  get spinStopDelay(): number {
    return this.configDto['spinDuration']['spinStopDelay'] / 1000 / this.speedMultiplier;
  }

  get continueDuration(): number {
    return this.configDto['spinDuration']['continueDuration'] / 1000;
  }

  get noWinDelay(): number {
    if (this.configDto['noWinDelay'] !== undefined) {
      return this.configDto['noWinDelay'];
    }

    return this.speedMultiplier === 1 ? 1 : 0;
  }

  get blinkDuration(): number {
    return this.configDto['wins']['blinkTime'] / 1000;
  }

  get blinkIterations(): number {
    return this.configDto['wins']['linesBlink'];
  }

  get singleEntitySpinSpeed(): number {
    const speed =
      (((this.configDto['spinSpeed'] / this.fpsCoef) * this.fps * this.displayHeight) / 640) *
      this.speedMultiplier;
    const SingleMaxSpeed =
      ((this.Engine.ReelConfig.symbolSize.y / this.fpsCoef) * this.fps * this.displayHeight) / 640;
    return speed > SingleMaxSpeed ? SingleMaxSpeed : speed;
  }

  get iconWithValuesConfig(): IconWithValuesConfig | null {
    return this.configDto['iconWithValuesParameters']
      ? new IconWithValuesConfig(this.configDto['iconWithValuesParameters'])
      : null;
  }

  get anticipationConfig(): AbstractAnticipationConfig {
    return new AnticipationConfig(
      this.configDto['anticipationParameters'],
      this.fps,
      this.displayHeight
    );
  }

  private get fps(): number {
    return REFERENCE_FPS;
  }
}

export class AnticipationConfig implements AbstractAnticipationConfig {
  public speedMultiplier = 1;

  private readonly _configDto: Record<string, any>;
  private readonly _fps: number;
  private readonly _displayHeight: number;

  constructor(configDto: Record<string, any>, fps: number, displayHeight: number) {
    this._configDto = configDto;
    this._fps = fps;
    this._displayHeight = displayHeight;
  }

  get anticipatedSpinSpeed(): number {
    return (
      ((this._configDto['spinSpeed'] * this._fps * this._displayHeight) / 640) *
      this.speedMultiplier
    );
  }

  get slowDownAnticipatedSpinSpeed(): number {
    return (
      (((this._configDto['spinSpeed'] / 4.0) * this._fps * this._displayHeight) / 640) *
      this.speedMultiplier
    );
  }

  get acceleratedDuration(): number {
    return 0.02;
  }

  get anticipationReels(): number[][] {
    return this._configDto['anticipationReels'];
  }

  get anticipationIcons(): number[] {
    return this._configDto['anticipationIcons'];
  }

  get minIconsForWin(): number {
    return this._configDto['anticipationMinIconsForWin'][0];
  }

  get minIconsForAnticipation(): number {
    return Array.isArray(this._configDto['minIconsForAnticipation']) &&
      this._configDto['minIconsForAnticipation'].length > 0 &&
      typeof this._configDto['minIconsForAnticipation'][0] === 'number'
      ? this._configDto['minIconsForAnticipation'][0]
      : 2;
  }

  get stopBehaviorReels(): number[][] {
    return this._configDto['stopBehaviorReels'];
  }

  get stopBehaviorIcons(): number[] {
    return this._configDto['stopBehaviorIcons'];
  }

  get stopBehaviorIconsByPos(): number[] {
    return this._configDto['stopBehaviorIconsByPos'];
  }

  get stopBehaviorConnectedIcons(): number[][] {
    return this._configDto['stopBehaviorConnectedIcons'];
  }

  get minIconsForStopBehavior(): number[] {
    return this._configDto['stopBehaviorMinIconsForWin'];
  }

  get continueDurationAnticipating(): number {
    return this._configDto['continueDurationAnticipating'] * 0.001;
  }

  get continueDurationNotAnticipating(): number {
    return this._configDto['continueDurationNotAnticipating'] * 0.001;
  }

  get maxSpeed(): number {
    return this._displayHeight * 30;
  }

  // Determines how many characters are needed to show anticipation
  minIconsForAnticipationForSymbol(symbol: number): number {
    const ancticipationIndex = this._configDto['anticipationIcons'].indexOf(symbol) ?? 0;
    if (ancticipationIndex === -1) return 2;
    return this._configDto['minIconsForAnticipation'] &&
      this._configDto['minIconsForAnticipation'].length > 0
      ? this._configDto['minIconsForAnticipation'][ancticipationIndex]
      : 2;
  }

  minIconsForWinForIcon(symbol: number): number {
    const ancticipationIndex = this._configDto['anticipationIcons'].indexOf(symbol) ?? 0;
    if (ancticipationIndex === -1) return 2;
    return this._configDto['anticipationMinIconsForWin'] &&
      this._configDto['anticipationMinIconsForWin'].length > 0
      ? this._configDto['anticipationMinIconsForWin'][ancticipationIndex]
      : 2;
  }
}

export class CollapsingParameters {
  public _spinSpeedMultiplier = 1;
  public _baseMultiplier = 1;
  private readonly _configDto: Record<string, any>;

  constructor(configDto: Record<string, any>) {
    this._configDto = configDto;
  }

  public set baseMultiplier(value: number) {
    this._baseMultiplier = value;
  }

  public get baseMultiplier(): number {
    return this._baseMultiplier;
  }

  public set spinSpeedMultiplier(value: number) {
    this._spinSpeedMultiplier = value;
  }

  public get spinSpeedMultiplier(): number {
    return this._spinSpeedMultiplier * this._baseMultiplier;
  }

  get iconFallingStartDelay(): number {
    return this._spinSpeedMultiplier > 1.0 ? 0.0 : this._configDto['iconFallingStartDelay'] * 0.001;
  }

  get reelFallingStartDelay(): number {
    return this._spinSpeedMultiplier > 1.0 ? 0.0 : this._configDto['reelFallingStartDelay'] * 0.001;
  }

  get iconOutgoingStartDelay(): number {
    return this._spinSpeedMultiplier > 1.0
      ? 0.0
      : this._configDto['iconOutgoingStartDelay'] * 0.001;
  }

  get roundFallingStartDelay(): number {
    return this._spinSpeedMultiplier > 1.0
      ? 0.0
      : this._configDto['roundFallingStartDelay'] * 0.001;
  }

  get iconShiftStartDelay(): number {
    return this._spinSpeedMultiplier > 1.0 ? 0.0 : this._configDto['iconShiftStartDelay'] * 0.001;
  }

  get fallSpinDelays(): number[] {
    return this._spinSpeedMultiplier > 1.0
      ? [0, 0, 0, 0, 0, 0]
      : this._configDto['fallStopDelays'].map((delay: number) => delay * 0.001);
  }

  get fallStopDelays(): number[] {
    return this._spinSpeedMultiplier > 1.0
      ? [0, 0, 0, 0, 0, 0]
      : this._configDto['fallStopDelays'].map((delay: number) => delay * 0.001);
  }
}

export class StaticConfig extends AbstractStaticConfig {
  private readonly _configDto: Record<string, any>;

  constructor(configDto: Record<string, any>) {
    super();
    this._configDto = configDto;
  }

  public get spinedReels(): number[][] {
    return this._configDto['reels'];
  }

  public get noWinReels(): number[][] {
    return this._configDto['noWinReels'];
  }
}

export class IconWithValuesConfig {
  private readonly _configDto: Record<string, any>;

  constructor(configDto: Record<string, any>) {
    this._configDto = configDto;
  }

  get specGroupTypeNames(): string[] {
    return this._configDto['specGroupTypeNames'];
  }

  get iconWithValueConfigs(): Map<number, IconWithValueConfig> | null {
    const icons = this._configDto['iconWithValueParameters'];
    if (!icons) {
      return null;
    }
    const result = new Map<number, IconWithValueConfig>();
    for (const key in icons) {
      const value = icons[key];
      const iconWithValue = new IconWithValueConfig(value);
      const newKey = parseInt(key);
      result.set(newKey, iconWithValue);
    }
    return result;
  }
}

export class IconWithValueConfig {
  private readonly _configDto: Record<string, any>;

  constructor(configDto: Record<string, any>) {
    this._configDto = configDto;
  }

  get shouldBeMultipliedByBet(): boolean {
    return !!this._configDto.shouldBeMultipliedByBet;
  }

  get useSubTypeForState(): boolean {
    return !!this._configDto.useSubTypeForState;
  }

  get possibleIconStates(): string[] | null {
    return this._configDto.possibleStates || null;
  }

  get values(): number[] | null {
    return this._configDto.baseValues || null;
  }
}

export class StagedFreeSpinsCollectConfig {
  private readonly _freeSpinsCollectConfigDto: Record<string, any>;

  constructor(featuresConfigDto: Record<string, any>) {
    this._freeSpinsCollectConfigDto = featuresConfigDto['stagedFreeSpinsCollectConfig'];
  }

  get stageLength(): number[] | null {
    return this._freeSpinsCollectConfigDto['stageLength'];
  }

  get stageEndValues(): number[] | null {
    return this._freeSpinsCollectConfigDto.getListOfDouble('stageEndValues');
  }
}

export class StackedReelsStopSlotActionConfig {
  private readonly _configDto: Record<string, any>;

  constructor(featuresConfigDto: Record<string, any>) {
    this._configDto = featuresConfigDto['stackedReelsStopSlotActionConfig'];
  }

  get showStacksHitRate(): number {
    return this._configDto['showStacksHitRate'];
  }

  get accelerateIfWinImpossible(): number {
    return this._configDto['accelerateIfWinImpossible'];
  }

  get setUpDistribution(): number {
    return this._configDto['setUpDistribution'];
  }
}
